import { useMemo, useRef, useEffect, useState } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import { motion } from "framer-motion";
import { Code2, GitBranch, Calendar, FileCode } from "lucide-react";
import { REPOS, LANGUAGE_TOTALS } from "../data/repos";

interface GNode {
  id: string;
  label: string;
  type: "repo" | "lang" | "topic";
  size: number;
  color: string;
  data?: any;
}
interface GLink {
  source: string;
  target: string;
  value: number;
}

const TOPIC_COLORS: Record<string, string> = {
  "infra-automation": "#60a5fa",
  "data-pipeline":    "#22d3ee",
  "ml":               "#a78bfa",
  "web":              "#f59e0b",
  "tooling":          "#fb7185",
};

export default function CodeGalaxy() {
  const fgRef = useRef<ForceGraphMethods<GNode, GLink>>();
  const [hover, setHover] = useState<GNode | null>(null);
  const [size, setSize] = useState({ w: 800, h: 560 });
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ w: Math.max(320, r.width), h: Math.max(420, r.height) });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const graph = useMemo(() => {
    const nodes: GNode[] = [];
    const links: GLink[] = [];

    // Topic anchors
    Object.entries(TOPIC_COLORS).forEach(([t, c]) => {
      nodes.push({ id: `topic:${t}`, label: t, type: "topic", size: 18, color: c });
    });

    // Repos
    REPOS.forEach((r) => {
      nodes.push({
        id: `repo:${r.name}`,
        label: r.name,
        type: "repo",
        size: 6 + Math.min(20, Math.log2(Math.max(2, r.loc))),
        color: TOPIC_COLORS[r.topic] || "#94a3b8",
        data: r,
      });
      links.push({ source: `topic:${r.topic}`, target: `repo:${r.name}`, value: 2 });

      // Connect repo to its top languages
      Object.entries(r.languages)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .forEach(([lang, share]) => {
          const langId = `lang:${lang}`;
          if (!nodes.find((n) => n.id === langId)) {
            const lt = LANGUAGE_TOTALS.find((l) => l.name === lang);
            nodes.push({
              id: langId,
              label: lang,
              type: "lang",
              size: 4 + (lt?.share ?? 0.01) * 80,
              color: lt?.color ?? "#64748b",
            });
          }
          links.push({ source: `repo:${r.name}`, target: langId, value: share });
        });
    });

    return { nodes, links };
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <div className="eyebrow">Layer · Code</div>
        <h1 className="text-3xl font-bold mt-1 tracking-tight">Code Galaxy</h1>
        <p className="text-slate-400 mt-2 max-w-3xl">
          Every owned repository, connected to its topics and languages. {REPOS.length} repos,
          {" "}{LANGUAGE_TOTALS.length} languages tracked. <span className="text-slate-500">No "Other" bucket.</span>
        </p>
      </header>

      {/* Force graph + hover panel */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div ref={wrapRef} className="xl:col-span-3 glass ring-glass relative h-[560px] overflow-hidden">
          <ForceGraph2D
            ref={fgRef as any}
            graphData={graph}
            width={size.w}
            height={size.h}
            backgroundColor="rgba(0,0,0,0)"
            nodeRelSize={4}
            linkColor={() => "rgba(255,255,255,0.08)"}
            linkWidth={(l: any) => 0.5 + (l.value || 1) * 1.5}
            onNodeHover={(n: any) => setHover(n)}
            nodeCanvasObject={(node: any, ctx, scale) => {
              const r = (node.size || 6) / scale * 1.2;
              ctx.beginPath();
              ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
              const grd = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, r * 2);
              grd.addColorStop(0, node.color);
              grd.addColorStop(1, "rgba(0,0,0,0)");
              ctx.fillStyle = grd;
              ctx.fill();
              ctx.beginPath();
              ctx.arc(node.x, node.y, r * 0.6, 0, 2 * Math.PI);
              ctx.fillStyle = node.color;
              ctx.fill();
              if (node.type === "repo" || node.type === "topic") {
                ctx.font = `${Math.max(9, 12/scale)}px Inter, sans-serif`;
                ctx.fillStyle = "rgba(226,232,240,0.85)";
                ctx.textAlign = "center";
                ctx.fillText(node.label, node.x, node.y + r * 1.6 + 4);
              }
            }}
            cooldownTicks={120}
            d3VelocityDecay={0.32}
          />
        </div>

        <aside className="glass p-5 ring-glass">
          <div className="eyebrow">{hover ? "Selected" : "Hover any node"}</div>
          {hover ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="block w-2.5 h-2.5 rounded-full" style={{ background: hover.color }} />
                <span className="font-mono text-xs text-slate-400">{hover.type.toUpperCase()}</span>
              </div>
              <h3 className="text-lg font-semibold">{hover.label}</h3>
              {hover.data && (
                <>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{hover.data.pitch}</p>
                  <div className="mt-3 space-y-1.5 text-xs font-mono">
                    <Row k="Path" v={hover.data.path} />
                    <Row k="Primary" v={hover.data.primaryLanguage} />
                    <Row k="Last Commit" v={hover.data.lastCommit} />
                    <Row k="Topic" v={hover.data.topic} />
                  </div>
                  {hover.data.notable?.length > 0 && (
                    <div className="mt-3">
                      <div className="eyebrow">Notable</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {hover.data.notable.map((n: string) => (
                          <span key={n} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.03] text-slate-300 font-mono">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          ) : (
            <p className="text-sm text-slate-500 mt-2">
              Each repo links to its top three languages and primary topic. Hover a node for detail.
            </p>
          )}
        </aside>
      </div>

      {/* Language totals bar */}
      <div className="glass p-6 ring-glass">
        <div className="eyebrow">Portfolio breakdown</div>
        <h2 className="text-xl font-semibold mt-1">Languages — full census</h2>
        <p className="text-xs text-slate-500 font-mono mt-1">
          {LANGUAGE_TOTALS.length} categories · sum {(LANGUAGE_TOTALS.reduce((a, l) => a + l.share, 0) * 100).toFixed(1)}%
        </p>
        <div className="mt-5 space-y-2">
          {LANGUAGE_TOTALS.map((l, i) => (
            <motion.div
              key={l.name}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.02 }}
              className="flex items-center gap-3"
            >
              <div className="w-28 text-xs font-mono text-slate-300">{l.name}</div>
              <div className="flex-1 relative h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, l.share * 100 * 3)}%` }}
                  transition={{ delay: 0.2 + i * 0.02, duration: 0.8, ease: "easeOut" }}
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ background: l.color }}
                />
              </div>
              <div className="w-14 text-right text-[11px] font-mono tabular-nums text-slate-400">
                {(l.share * 100).toFixed(1)}%
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Repo cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPOS.map((r, i) => (
          <motion.a
            key={r.name}
            href={`https://${r.remote}`}
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="glass glass-hover p-5 block group"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/[0.04] flex items-center justify-center" style={{ color: TOPIC_COLORS[r.topic] }}>
                <Code2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.name}</div>
                <div className="eyebrow mt-0.5 truncate">{r.topic}</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3 leading-relaxed line-clamp-3">{r.pitch}</p>
            <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-slate-500">
              <span className="flex items-center gap-1"><FileCode size={10} />{r.primaryLanguage}</span>
              <span className="flex items-center gap-1"><Calendar size={10} />{r.lastCommit}</span>
              <span className="flex items-center gap-1"><GitBranch size={10} />{r.deployable ? "deployable" : "static"}</span>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-300 truncate">{v}</span>
    </div>
  );
}
